/**
 * R-6.5: i18n foundation for APAC multi-language support.
 *
 * All locale JSON files under ./locales/ are registered here. Language
 * detection order: localStorage (via the "vynahealth-language" key) →
 * browser navigator → <html lang>. Persisted choice wins so a
 * returning user keeps their last-selected language.
 *
 * Adding a new locale:
 *   1. Drop the JSON file into ./locales/<lang>.json matching the
 *      key shape of en.json.
 *   2. Import + register it below.
 *   3. Add it to SUPPORTED_LANGUAGES so the switcher picks it up.
 *
 * Usage:
 *   import { useTranslation } from 'react-i18next';
 *   const { t } = useTranslation();
 *   <h1>{t('navigation.dashboard')}</h1>
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import en from './locales/en.json'
import enSG from './locales/en-SG.json'
import hi from './locales/hi.json'
import id from './locales/id.json'
import ja from './locales/ja.json'
import ko from './locales/ko.json'
import ms from './locales/ms.json'
import th from './locales/th.json'
import vi from './locales/vi.json'
import zhCN from './locales/zh-CN.json'
import zhTW from './locales/zh-TW.json'

// Ordered for the switcher UI: English variants first, then APAC
// languages by script (CJK, then Latin-script SEA, then Indic).
export const SUPPORTED_LANGUAGES = [
  { code: 'en',    label: 'English' },
  { code: 'en-SG', label: 'English (SG)' },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'ja',    label: '日本語' },
  { code: 'ko',    label: '한국어' },
  { code: 'vi',    label: 'Tiếng Việt' },
  { code: 'th',    label: 'ไทย' },
  { code: 'id',    label: 'Bahasa Indonesia' },
  { code: 'ms',    label: 'Bahasa Melayu' },
  { code: 'hi',    label: 'हिन्दी' },
] as const

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code']

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en:      { translation: en },
      'en-SG': { translation: enSG },
      hi:      { translation: hi },
      id:      { translation: id },
      ja:      { translation: ja },
      ko:      { translation: ko },
      ms:      { translation: ms },
      th:      { translation: th },
      vi:      { translation: vi },
      'zh-CN': { translation: zhCN },
      'zh-TW': { translation: zhTW },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'vynahealth-language',
    },
  })

export default i18n
